import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { showBanner } from '../../../global-state/banner-slice'
import { Button, Flex, InputModal, MultiInputFile, RadioInput } from '../'
import { kycServices } from '../../../services/kyc-service'
import {
  ExtendLimitPayload,
  InvestmentLimitDocumentType,
  InvestmentLimitRange,
} from '../../../services/apiTypes_v1'
import './extend-limit-modal.scss'
import { getInvestorDataFromSS } from '../../utils/local-storage'
import {
  ALREADY_SUBMITTED_LIMIT_REQUEST_CODE,
  ALREADY_SUBMITTED_LIMIT_REQUEST_MESSAGE,
  getApiErrorCode,
} from '../../utils/use-pending-investment-limit-request'

interface ExtendLimitModalProps {
  onClose: () => void
  onSubmitted?: () => void
}

export const INVESTMENT_LIMIT_OPTIONS = [
  {
    name: 'requestedInvestmentLimit',
    value: 'USD_1001_15000',
    text: 'USD 1,001 – USD 15,000',
  },
  {
    name: 'requestedInvestmentLimit',
    value: 'USD_15001_50000',
    text: 'USD 15,001 – USD 50,000',
  },
  {
    name: 'requestedInvestmentLimit',
    value: 'USD_50001_250000',
    text: 'USD 50,001 – USD 250,000',
  },
  {
    name: 'requestedInvestmentLimit',
    value: 'USD_250000_PLUS',
    text: 'USD 250,000 and above',
  },
]

export const ACCEPTED_SUPPORTING_DOCUMENTS = [
  'Additional bank account statements not more than 6 months old',
  'Net worth certificates issued by a qualified professional',
  'Tax returns or tax assessments for the last FY',
  'Recent payslip',
  'Investment portfolio statements (brokerage, demat, custody, mutual funds, securities, etc.)',
  'Fixed deposits / Bonds Certificates',
  'For business owners: recent audited financial statements and evidence of dividend distributions where applicable',
  'Trust or inheritance documents where wealth has been inherited',
  'Evidence of proceeds from the sale of property, shares, businesses, or other significant assets',
  'Dividend statements or partnership distribution statements',
]

const ACCEPTED_FILE_TYPES = ['pdf', 'image', 'doc', 'xls'] as const
const MAX_DOCUMENTS = 20
const MAX_FILE_SIZE_MB = 5
const MAX_TOTAL_SIZE_BYTES = 30 * 1024 * 1024

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result
      if (typeof result !== 'string') {
        reject(new Error('Unable to read file'))
        return
      }
      resolve(result.includes(',') ? result.slice(result.indexOf(',') + 1) : result)
    }
    reader.onerror = () => reject(reader.error ?? new Error('Unable to read file'))
    reader.readAsDataURL(file)
  })

const ExtendLimitModal = ({ onClose, onSubmitted }: ExtendLimitModalProps) => {
  const dispatch = useDispatch()
  const [requestedLimit, setRequestedLimit] = useState<InvestmentLimitRange | ''>('')
  const [documentsByType, setDocumentsByType] = useState<
    Partial<Record<InvestmentLimitDocumentType, File[]>>
  >({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const selectedDocuments = Object.entries(documentsByType).flatMap(([documentType, files]) =>
    (files ?? []).map((file) => ({
      documentType: documentType as InvestmentLimitDocumentType,
      file,
    })),
  )
  const bankStatementFiles = documentsByType.BANK_STATEMENTS_6_MONTHS ?? []
  const totalSize = selectedDocuments.reduce((sum, { file }) => sum + file.size, 0)
  const canSubmit =
    Boolean(requestedLimit) &&
    bankStatementFiles.length > 0 &&
    selectedDocuments.length <= MAX_DOCUMENTS &&
    totalSize <= MAX_TOTAL_SIZE_BYTES &&
    !isSubmitting

  const setFilesForType = (documentType: InvestmentLimitDocumentType, files?: File[]) => {
    setDocumentsByType((current) => ({
      ...current,
      [documentType]: files ?? [],
    }))
  }

  const handleRequestExtension = async () => {
    if (!canSubmit) {
      return
    }

    setIsSubmitting(true)

    try {
      const documents = await Promise.all(
        selectedDocuments.map(async ({ documentType, file }) => ({
          documentType,
          fileName: file.name,
          mimeType: file.type || 'application/octet-stream',
          base64Content: await fileToBase64(file),
        })),
      )
      const payload: ExtendLimitPayload = {
        requestedLimitRange: requestedLimit as InvestmentLimitRange,
        documents,
      }
      const response = await kycServices.extendLimit(payload, {
        Authorization: `Bearer ${getInvestorDataFromSS().authToken}`,
        'Content-Type': 'application/json',
      })

      if (response.status === 200) {
        dispatch(
          showBanner({
            text: 'Investment limit extension request submitted successfully',
            variant: 'success-banner',
            duration: 5000,
          }),
        )

        onSubmitted?.()
        onClose()
        return
      }

      throw new Error('Investment limit extension request failed')
    } catch (error) {
      console.error('Error requesting limit extension:', error)
      const alreadySubmitted = getApiErrorCode(error) === ALREADY_SUBMITTED_LIMIT_REQUEST_CODE
      dispatch(
        showBanner({
          text: alreadySubmitted
            ? ALREADY_SUBMITTED_LIMIT_REQUEST_MESSAGE
            : 'Failed to request investment limit extension. Please try again.',
          variant: alreadySubmitted ? 'warning-banner' : 'error-banner',
          duration: 5000,
        }),
      )
      if (alreadySubmitted) {
        onSubmitted?.()
        onClose()
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <InputModal testId='extend-limit-modal' onBackdropClick={onClose}>
      <Flex variant='column' classes={['extend-limit-modal', 'extend-limit-modal-wrapper']}>
        <h2>Place a request to increase the Investment Limit</h2>
        <p className='basic mt-14'>
          Tell us the limit you would like and upload documents that support your expected
          transaction value.
        </p>

        <RadioInput
          classes={['extend-limit-modal__radios', 'mt-24']}
          labelText='What investment limit would you like to increase to?'
          list={INVESTMENT_LIMIT_OPTIONS}
          defaultValue={requestedLimit}
          handleChange={(value) => setRequestedLimit(value as InvestmentLimitRange)}
        />

        <div className='extend-limit-modal__documents mt-24'>
          <h3 className='extend-limit-modal__section-title'>
            Please submit the documents that support your expected transaction value:
          </h3>

          <div className='extend-limit-modal__required-doc'>
            <p className='extend-limit-modal__doc-label'>
              Bank account statement not more than 6 months old
              <span className='extend-limit-modal__required-tag'>Required</span>
            </p>
            <p className='extend-limit-modal__doc-hint'>
              You can select several files at once, or add more with another upload.
            </p>
            <MultiInputFile
              name='extendLimitBankStatement'
              label=''
              type={[...ACCEPTED_FILE_TYPES]}
              limit={MAX_DOCUMENTS}
              maxFileSizeInMB={MAX_FILE_SIZE_MB}
              handleFiles={(files) =>
                setFilesForType('BANK_STATEMENTS_6_MONTHS', (files ?? []) as File[])
              }
              dataTestId='extend-limit-bank-statement'
            />
          </div>

          <p className='extend-limit-modal__optional-intro'>
            Note: If your bank account statement does not reflect a closing balance sufficient to
            support the intended investment, please provide additional documents (as applicable)
            demonstrating the source of funds to undertake the investment.
          </p>

          <div className='extend-limit-modal__accepted-docs'>
            <p className='extend-limit-modal__accepted-docs-title'>
              Other supporting documents we accept (optional)
            </p>
            <ul>
              {ACCEPTED_SUPPORTING_DOCUMENTS.map((document) => (
                <li key={document}>{document}</li>
              ))}
            </ul>
          </div>

          <div className='extend-limit-modal__optional-upload'>
            <p className='extend-limit-modal__doc-label'>Other supporting documents</p>
            <p className='extend-limit-modal__doc-hint'>
              Optional. You can select several files at once — up to {MAX_DOCUMENTS} documents in
              total, {MAX_FILE_SIZE_MB}MB per file.
            </p>
            <MultiInputFile
              name='extendLimitSupportingDocuments'
              label=''
              type={[...ACCEPTED_FILE_TYPES]}
              limit={MAX_DOCUMENTS}
              maxFileSizeInMB={MAX_FILE_SIZE_MB}
              handleFiles={(files) =>
                setFilesForType('OTHER_SUPPORTING_DOCUMENT', (files ?? []) as File[])
              }
              dataTestId='extend-limit-supporting-documents'
            />
          </div>
          {selectedDocuments.length > MAX_DOCUMENTS || totalSize > MAX_TOTAL_SIZE_BYTES ? (
            <p className='extend-limit-modal__validation-error' role='alert'>
              Upload up to 20 documents with a combined size of 30MB or less.
            </p>
          ) : null}
        </div>

        <Flex variant='row' classes={['mt-24', 'button-container']}>
          <Button
            text='Cancel'
            variant='secondary-btn'
            handleClick={onClose}
            testId='extend-limit-cancel'
            isDisabled={isSubmitting}
          />
          <Button
            text={isSubmitting ? 'Submitting...' : 'Submit request'}
            variant='primary-btn'
            handleClick={handleRequestExtension}
            testId='extend-limit-submit'
            isDisabled={!canSubmit}
          />
        </Flex>
      </Flex>
    </InputModal>
  )
}

export default ExtendLimitModal
